import { useQuery } from '@tanstack/react-query'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  AdminAlert, AdminAlertsResponse, AlertSeverity,
} from '../api/types'
import { RelativeTime } from './RelativeTime'

// plan-admin/10 — Alerts panel mounted at the top of the Health page.
//
// Refresh interval matches the rest of the SPA's polling cadence
// (60s); the backend caches the evaluator for ~30s so the per-poll
// cost stays trivial. Sorted critical → warning → info; the backend
// already emits the right order, but the panel re-sorts defensively
// so a future backend change does not silently bury a critical alert.

const SEVERITY_RANK: Record<AlertSeverity, number> = {
  critical: 0, warning: 1, info: 2,
}

const SEVERITY_STYLE: Record<AlertSeverity, string> = {
  critical: 'border-red-300 bg-red-50 text-red-900',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  info: 'border-stone-200 bg-stone-50 text-stone-800',
}

const SEVERITY_LABEL_STYLE: Record<AlertSeverity, string> = {
  critical: 'bg-red-200 text-red-900',
  warning: 'bg-amber-200 text-amber-900',
  info: 'bg-stone-200 text-stone-800',
}

export function AlertsPanel() {
  const client = useAdminApiClient()
  const query = useQuery({
    queryKey: queryKeys.alerts(),
    queryFn: () => client.get<AdminAlertsResponse>('/r/admin/alerts'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })

  if (query.isLoading) {
    return (
      <section
        aria-label="Active alerts"
        data-testid="alerts-loading"
        className="rounded-lg border border-stone-200 bg-white p-4 text-sm
                   text-stone-500"
      >
        Loading alerts…
      </section>
    )
  }
  if (query.isError || !query.data?.success) {
    return (
      <section
        aria-label="Active alerts"
        data-testid="alerts-error"
        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm
                   text-red-800"
        role="alert"
      >
        <p className="font-semibold">Failed to load alerts.</p>
        <p className="mt-1 text-xs text-red-700">
          The backend returned an error. Active alerts cannot be
          displayed right now; the daemon and Mailgun pipelines are
          still emitting metrics.
        </p>
      </section>
    )
  }

  const alerts = [...query.data.data.alerts].sort(
    (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
  )

  if (alerts.length === 0) {
    return (
      <section
        aria-label="Active alerts"
        className="rounded-lg border border-emerald-200 bg-emerald-50 p-4
                   text-sm text-emerald-900"
      >
        <span className="font-semibold">No active alerts.</span>{' '}
        All daemon and Mailgun signals look healthy.
      </section>
    )
  }

  return (
    <section
      aria-label="Active alerts"
      className="space-y-2"
    >
      {alerts.map((alert) => (
        <AlertCard key={alert.id} alert={alert} />
      ))}
    </section>
  )
}

interface AlertCardProps {
  alert: AdminAlert
}

function AlertCard({ alert }: AlertCardProps) {
  return (
    <article
      data-testid={`alert-${alert.id}`}
      data-severity={alert.severity}
      className={[
        'flex flex-col gap-1 rounded-lg border p-3 text-sm shadow-sm',
        SEVERITY_STYLE[alert.severity],
      ].join(' ')}
    >
      <header className="flex flex-wrap items-baseline gap-2">
        <span
          className={[
            'rounded-full px-2 py-0.5 text-xs font-semibold uppercase',
            SEVERITY_LABEL_STYLE[alert.severity],
          ].join(' ')}
        >
          {alert.severity}
        </span>
        <span className="font-mono text-xs">{alert.id}</span>
        <span className="ml-auto text-xs">
          <RelativeTime timestamp={alert.triggered_time} />
        </span>
      </header>
      <p className="font-medium">{alert.summary}</p>
      {alert.remediation && (
        <p className="text-xs">{alert.remediation}</p>
      )}
    </article>
  )
}
